import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, Loader2 } from 'lucide-react';
import {
  fetchProposalCategories,
  buildProposalCategoryCards,
  getCategoryIcon,
  getCategoryColors,
  routeForCategory,
} from '../../lib/proposalCategories';

export default function ProposalCategoryPanel({ sectionTitle = 'Choose Your Proposal Type' }) {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchProposalCategories();
        if (!cancelled) setCategories(buildProposalCategoryCards(list));
      } catch (e) {
        if (!cancelled) setError('Could not load proposal categories.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSelect = (cat) => {
    const route = routeForCategory(cat);
    if (route) navigate(route);
  };

  return (
    <div className="space-y-6" data-testid="proposal-category-panel">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-foreground">{sectionTitle}</h2>
        <p className="text-muted-foreground">
          Pick a category — we&apos;ll show templates and form fields relevant to that proposal type.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground py-8" data-testid="category-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading categories…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive" data-testid="category-error">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat) => {
            const Icon = getCategoryIcon(cat.icon);
            const colors = getCategoryColors(cat.color);
            const isBusinessPlan = cat.category_id === 'business_plan_funding';
            const comingSoon = !isBusinessPlan && (cat.coming_soon_only || !cat.is_available);
            const creditLabel = isBusinessPlan ? 20 : cat.credit_cost;
            return (
              <Card
                key={cat.category_id}
                className={[
                  'group transition-all',
                  comingSoon
                    ? 'cursor-default opacity-60 grayscale-[0.15]'
                    : 'cursor-pointer hover:border-primary hover:shadow-md',
                ].join(' ')}
                onClick={() => handleSelect(cat)}
                data-testid={`category-card-${cat.category_id}`}
              >
                <CardContent className="p-6">
                  <div className="mb-3 flex items-start justify-between">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${colors.bg} ring-1 ${colors.ring}`}>
                      <Icon className={`h-6 w-6 ${colors.text}`} />
                    </div>
                    {comingSoon
                      ? <Badge variant="outline" className="text-amber-700"><Clock className="mr-1 h-3 w-3" /> Coming Soon</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Available · {creditLabel} cr</Badge>}
                  </div>
                  <h3 className="text-base font-semibold leading-tight text-foreground">{cat.display_name}</h3>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{cat.description}</p>
                  {!comingSoon && (
                    <div className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                      Start →
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
